import axiosInterceptor from '../../utils/axios/axiosInterceptor';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { projectRunBasePath } from '../../services/projectRunsApi';
import { downloadQueryLogReportPdf } from '../../services/queryLogReplayApi';
import {
  ViewReportState,
  ReportState,
  QueryRow,
  QuerySummary,
  QueryFetchParams,
  QueryDataApiResponse,
  ReportMetadata,
  ApiQuery,
  DownloadQueryReportResponse,
  CategoryCountsApiResponse,
  CategoryFilterType,
} from '../../types/viewReports';

const api = () => axiosInterceptor(true);

const PAGE_SIZE = 10;

// Helper to transform API query to UI QueryRow
const transformApiQuery = (apiQuery: ApiQuery): QueryRow => {
  const row: QueryRow = {
    id: apiQuery.query_id,
    query: apiQuery.query_text,
  };

  // Process execution summaries
  apiQuery.execution_summaries.forEach((summary) => {
    if (summary.data_size === '1x') {
      row.time1x = {
        min: summary.min_execution_time,
        max: summary.max_execution_time,
        avg: summary.avg_execution_time,
        stdDev: summary.std_deviation,
      };
    } else if (summary.data_size === '10x') {
      row.time10x = {
        min: summary.min_execution_time,
        max: summary.max_execution_time,
        avg: summary.avg_execution_time,
        stdDev: summary.std_deviation,
      };
    }
  });

  return row;
};

// Helper to create initial report state
const createInitialReportState = (metadata: ReportMetadata): ReportState => ({
  id: metadata.id,
  title: metadata.title,
  description: metadata.description,
  date: metadata.date,
  type: metadata.type,
  queries: [],
  querySummary: metadata.querySummary,
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  currentPage: 0,
  totalCount: 0,
  error: null,
  searchText: '',
  sortBy: null,
  sortDirection: 'asc',
  performanceFilter: 'all',
});

// Helper to initialize reports for seq and conc
export const initializeReports = (): {
  reports: Record<string, ReportState>;
  reportIds: string[];
} => {
  const seqReport = createInitialReportState({
    id: 'sequential',
    title: 'Sequential Execution',
    description:
      'This report contains query logs from sequential execution tests, measuring performance under single-threaded conditions.',
    date: new Date().toISOString(),
    type: 'seq',
    querySummary: { green: 0, yellow: 0, red: 0, total: 0 },
  });

  const concReport = createInitialReportState({
    id: 'concurrent',
    title: '100 Concurrent Execution',
    description:
      'This report contains query logs from 100 concurrent execution tests, measuring performance under high-load conditions.',
    date: new Date().toISOString(),
    type: 'conc',
    querySummary: { green: 0, yellow: 0, red: 0, total: 0 },
  });

  return {
    reports: {
      sequential: seqReport,
      concurrent: concReport,
    },
    reportIds: ['sequential', 'concurrent'],
  };
};

const initialState: ViewReportState = {
  ...initializeReports(),
  error: null,
  isDownloading: false,
  downloadError: null,
};

const viewReportSlice = createSlice({
  name: 'viewReport',
  initialState,
  reducers: {
    // Initialize reports
    initReports(state) {
      const { reports, reportIds } = initializeReports();
      state.reports = reports;
      state.reportIds = reportIds;
    },

    // Report loading states
    setReportLoading(
      state,
      action: PayloadAction<{ reportId: string; isLoading: boolean }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.isLoading = action.payload.isLoading;
      }
    },
    setReportLoadingMore(
      state,
      action: PayloadAction<{ reportId: string; isLoadingMore: boolean }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.isLoadingMore = action.payload.isLoadingMore;
      }
    },
    setReportError(
      state,
      action: PayloadAction<{ reportId: string; error: string | null }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.error = action.payload.error;
      }
    },

    // Set queries (initial load - replaces existing)
    setReportQueries(
      state,
      action: PayloadAction<{
        reportId: string;
        queries: QueryRow[];
        totalCount: number;
        hasMore: boolean;
        querySummary?: QuerySummary;
        searchText?: string;
      }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.queries = action.payload.queries;
        report.totalCount = action.payload.totalCount;
        report.hasMore = action.payload.hasMore;
        report.currentPage = 1;
        if (action.payload.querySummary) {
          report.querySummary = action.payload.querySummary;
        }
        if (action.payload.searchText !== undefined) {
          report.searchText = action.payload.searchText;
        }
        report.isLoading = false;
      }
    },

    // Append queries (load more - adds to existing)
    appendReportQueries(
      state,
      action: PayloadAction<{
        reportId: string;
        queries: QueryRow[];
        hasMore: boolean;
        page: number;
      }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.queries = [...report.queries, ...action.payload.queries];
        report.hasMore = action.payload.hasMore;
        report.currentPage = action.payload.page;
        report.isLoadingMore = false;
      }
    },

    // Filter/sort setters
    setSortBy(
      state,
      action: PayloadAction<{ reportId: string; sortBy: '1x' | '10x' | 'Ratio' | null }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.sortBy = action.payload.sortBy;
      }
    },
    setSortDirection(
      state,
      action: PayloadAction<{ reportId: string; sortDirection: 'asc' | 'desc' }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.sortDirection = action.payload.sortDirection;
      }
    },
    setPerformanceFilter(
      state,
      action: PayloadAction<{
        reportId: string;
        filter: 'all' | 'green' | 'yellow' | 'red';
      }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.performanceFilter = action.payload.filter;
      }
    },

    // Reset report queries (before refetch with new filters)
    resetReportQueries(state, action: PayloadAction<string>) {
      const report = state.reports[action.payload];
      if (report) {
        report.queries = [];
        report.currentPage = 0;
        report.hasMore = true;
      }
    },

    // Download state reducers
    setDownloading(state, action: PayloadAction<boolean>) {
      state.isDownloading = action.payload;
    },
    setDownloadError(state, action: PayloadAction<string | null>) {
      state.downloadError = action.payload;
    },

    // Set query summary (from category counts API)
    setQuerySummary(
      state,
      action: PayloadAction<{ reportId: string; querySummary: QuerySummary }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.querySummary = action.payload.querySummary;
      }
    },

    setReportDate(
      state,
      action: PayloadAction<{ reportId: string; date: string }>
    ) {
      const report = state.reports[action.payload.reportId];
      if (report) {
        report.date = action.payload.date;
      }
    },
  },
});

// Async Actions

/**
 * Fetch queries for a report (initial load or after filter change)
 * Uses the new query-report endpoint
 */
export function fetchReportQueries(
  projectId: number,
  runId: number | null,
  reportId: string,
  params: {
    sortBy?: '1x' | '10x' | 'Ratio' | null;
    sortDirection?: 'asc' | 'desc';
    performanceFilter?: 'all' | 'green' | 'yellow' | 'red';
    searchQuery?: string;
  } = {}
) {
  return async () => {
    dispatch(setReportLoading({ reportId, isLoading: true }));
    dispatch(setReportError({ reportId, error: null }));
    dispatch(resetReportQueries(reportId));

    // Determine report type from reportId
    const reportType: 'seq' | 'conc' = reportId.includes('concurrent')
      ? 'conc'
      : 'seq';

    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(
        setReportError({
          reportId,
          error: 'No performance run selected for this project.',
        })
      );
      dispatch(setReportLoading({ reportId, isLoading: false }));
      return;
    }

    // Build query params for new endpoint
    const queryParams: Partial<QueryFetchParams> = {
      page_number: 1,
      page_size: PAGE_SIZE,
      type: reportType,
    };

    // Add optional sort params
    if (params.sortBy) {
      // Map 'Ratio' to 'ratio_10x_1x' for API
      queryParams.sort_data_size = params.sortBy === 'Ratio' ? 'ratio_10x_1x' : params.sortBy;
      queryParams.order_by = 'avg_execution_time';
      queryParams.order = params.sortDirection || 'asc';
    }

    // Add search query param
    if (params.searchQuery && params.searchQuery.trim()) {
      queryParams.search_query = params.searchQuery.trim();
    }

    // Add category filter param (green -> good, yellow -> average, red -> bad)
    if (params.performanceFilter && params.performanceFilter !== 'all') {
      queryParams.category_filter = performanceFilterToCategoryFilter(
        params.performanceFilter
      );
    }

    try {
      const response = await api().get<QueryDataApiResponse>(
        `${base}/query-report`,
        { params: queryParams }
      );

      // Transform API response to UI format
      const queries = response.data.project.queries.map(transformApiQuery);

      if (response.data.project.report_generated_at) {
        dispatch(
          setReportDate({
            reportId,
            date: response.data.project.report_generated_at,
          })
        );
      }

      dispatch(
        setReportQueries({
          reportId,
          queries,
          totalCount: response.data.total_count ?? queries.length,
          hasMore: response.data.has_more ?? queries.length === PAGE_SIZE,
          searchText: params.searchQuery || '',
        })
      );
    } catch (error: any) {
      dispatch(
        setReportError({
          reportId,
          error: error.message || 'Failed to fetch queries',
        })
      );
      dispatch(setReportLoading({ reportId, isLoading: false }));
    }
  };
}

/**
 * Load more queries for a report (pagination)
 */
export function loadMoreQueries(
  projectId: number,
  runId: number | null,
  reportId: string,
  currentPage: number,
  params: {
    sortBy?: '1x' | '10x' | 'Ratio' | null;
    sortDirection?: 'asc' | 'desc';
    performanceFilter?: 'all' | 'green' | 'yellow' | 'red';
    searchQuery?: string;
  } = {}
) {
  return async () => {
    dispatch(setReportLoadingMore({ reportId, isLoadingMore: true }));

    // Determine report type from reportId
    const reportType: 'seq' | 'conc' = reportId.includes('concurrent')
      ? 'conc'
      : 'seq';

    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(setReportLoadingMore({ reportId, isLoadingMore: false }));
      return;
    }

    const nextPage = currentPage + 1;

    // Build query params for new endpoint
    const queryParams: Partial<QueryFetchParams> = {
      page_number: nextPage,
      page_size: PAGE_SIZE,
      type: reportType,
    };

    // Add optional sort params
    if (params.sortBy) {
      // Map 'Ratio' to 'ratio_10x_1x' for API
      queryParams.sort_data_size = params.sortBy === 'Ratio' ? 'ratio_10x_1x' : params.sortBy;
      queryParams.order_by = 'avg_execution_time';
      queryParams.order = params.sortDirection || 'asc';
    }

    // Add search query param
    if (params.searchQuery && params.searchQuery.trim()) {
      queryParams.search_query = params.searchQuery.trim();
    }

    // Add category filter param (green -> good, yellow -> average, red -> bad)
    if (params.performanceFilter && params.performanceFilter !== 'all') {
      queryParams.category_filter = performanceFilterToCategoryFilter(
        params.performanceFilter
      );
    }

    try {
      const response = await api().get<QueryDataApiResponse>(
        `${base}/query-report`,
        { params: queryParams }
      );

      // Transform API response to UI format
      const queries = response.data.project.queries.map(transformApiQuery);

      dispatch(
        appendReportQueries({
          reportId,
          queries,
          hasMore: response.data.has_more ?? queries.length === PAGE_SIZE,
          page: nextPage,
        })
      );
    } catch (error: any) {
      dispatch(
        setReportError({
          reportId,
          error: error.message || 'Failed to load more queries',
        })
      );
      dispatch(setReportLoadingMore({ reportId, isLoadingMore: false }));
    }
  };
}

/**
 * Download the pre-generated performance report PDF from S3.
 */
export function downloadPerformanceReport(
  projectId: number,
  runId: number | null
) {
  return async () => {
    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(setDownloadError('No performance run selected for this project.'));
      return;
    }

    dispatch(setDownloading(true));
    dispatch(setDownloadError(null));

    try {
      const response = await api().get<DownloadQueryReportResponse>(
        `${base}/download-query-report`
      );

      if (response.data.status === 'success' && response.data.url) {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = response.data.filename || 'performance_report.pdf';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        dispatch(setDownloadError('Failed to get download URL'));
      }
    } catch (error: any) {
      dispatch(
        setDownloadError(
          error?.response?.data?.message ||
            error.message ||
            'Failed to download performance report'
        )
      );
      throw error;
    } finally {
      dispatch(setDownloading(false));
    }
  };
}

/** Download the pre-generated query log analysis PDF for the latest completed replay. */
export function downloadQueryLogReport(
  projectId: number,
  runId: number | null
) {
  return async () => {
    if (runId == null || runId < 1) {
      dispatch(setDownloadError('No performance run selected for this project.'));
      return;
    }

    dispatch(setDownloading(true));
    dispatch(setDownloadError(null));

    try {
      await downloadQueryLogReportPdf(projectId, runId);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      dispatch(
        setDownloadError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to download query log report'
        )
      );
      throw error;
    } finally {
      dispatch(setDownloading(false));
    }
  };
}

/** @deprecated Use downloadPerformanceReport — kept for callers that still pass report IDs. */
export function downloadQueryReport(
  projectId: number,
  runId: number | null,
  _selectedReports?: string[]
) {
  return downloadPerformanceReport(projectId, runId);
}

/**
 * Convert UI performance filter to API category_filter
 * green -> good, yellow -> average, red -> bad
 */
function performanceFilterToCategoryFilter(
  filter: 'all' | 'green' | 'yellow' | 'red'
): CategoryFilterType | undefined {
  switch (filter) {
    case 'green':
      return 'good';
    case 'yellow':
      return 'average';
    case 'red':
      return 'bad';
    default:
      return undefined;
  }
}

/**
 * Fetch category counts for a report (green/yellow/red filter counts)
 * GET /project/query-report-category-counts
 */
export function fetchCategoryCounts(projectId: number, runId: number | null, reportId: string) {
  return async () => {
    // Determine report type from reportId
    const executionType: 'seq' | 'conc' = reportId.includes('concurrent')
      ? 'conc'
      : 'seq';

    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      return;
    }

    try {
      const response = await api().get<CategoryCountsApiResponse>(
        `${base}/query-report-category-counts`,
        {
          params: {
            project_id: projectId,
            execution_type: executionType,
          },
        }
      );

      const querySummary: QuerySummary = {
        green: response.data.well_scaled_queries,
        yellow: response.data.moderately_scaled_queries,
        red: response.data.poorly_scaled_queries,
        total:
          response.data.well_scaled_queries +
          response.data.moderately_scaled_queries +
          response.data.poorly_scaled_queries,
      };

      dispatch(setQuerySummary({ reportId, querySummary }));
    } catch (error: any) {
      console.error('Failed to fetch category counts:', error);
      // Don't set error state - this is a non-critical API call
    }
  };
}

export const {
  initReports,
  setReportLoading,
  setReportLoadingMore,
  setReportError,
  setReportQueries,
  appendReportQueries,
  setSortBy,
  setSortDirection,
  setPerformanceFilter,
  resetReportQueries,
  setDownloading,
  setDownloadError,
  setQuerySummary,
  setReportDate,
} = viewReportSlice.actions;

export default viewReportSlice.reducer;
